/* CaseNotesJournal — case-scoped private notes timeline.
 *
 * Used on the planner case workspace. Mirrors the visual design of
 * `PatientNotesSection` (Clinical notes section on the patient profile)
 * so doctors and planners see a consistent UI even though the underlying
 * data is different.
 *
 * Data model: append-only entries (id, text, created_at, author_name)
 * scoped to a case_id. Endpoints under /cases/{id}/planner-notes/entries
 * (and /doctor-notes/entries for the doctor case page if ever wired here
 * directly — today the doctor case page reuses PatientNotesSection).
 *
 * Props:
 *   caseId   — required
 *   role     — "doctor" | "planner"
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    Note, Plus, Stethoscope, X as CloseIcon,
} from "@phosphor-icons/react";

const ENDPOINTS = {
    doctor:  "/doctor-notes/entries",
    planner: "/planner-notes/entries",
};

export default function CaseNotesJournal({ caseId, role = "planner" }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composerOpen, setComposerOpen] = useState(false);

    const endpointBase = ENDPOINTS[role];

    const load = async () => {
        if (!caseId) return;
        setLoading(true);
        try {
            const r = await api.get(`/cases/${caseId}${endpointBase}`);
            setEntries(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            if (e.response?.status !== 403) toast.error("Couldn't load notes");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseId]);

    const onSaved = () => { setComposerOpen(false); load(); };

    const subtitle = `Private case notes · ${entries.length} note${entries.length === 1 ? "" : "s"}`;

    return (
        <div data-testid={`case-notes-journal-${role}`} className="bg-card border border-border rounded-2xl bento-shadow overflow-hidden">
            <div className="px-5 sm:px-6 py-3.5 flex items-start justify-between gap-3 border-b border-border bg-secondary/30">
                <div>
                    {/* iter98.99cd — Tab header matches the Doctor's
                        Submission + Author-the-plan tabs: small CAPS
                        tracked title + italic subhead. */}
                    <h3 className="text-[11px] font-bold tracking-[0.16em] uppercase text-foreground">
                        My Notes
                    </h3>
                    <p className="text-[12px] italic text-muted-foreground mt-0.5">
                        {subtitle} · only you can see these
                    </p>
                </div>
                {!composerOpen && (
                    <Button
                        size="sm"
                        onClick={() => setComposerOpen(true)}
                        className="rounded-full"
                        data-testid={`case-notes-add-${role}`}
                    >
                        <Plus size={13} weight="bold" className="mr-1" /> Add note
                    </Button>
                )}
            </div>

            {composerOpen && (
                <NoteComposer
                    caseId={caseId}
                    endpointBase={endpointBase}
                    onCancel={() => setComposerOpen(false)}
                    onSaved={onSaved}
                    role={role}
                />
            )}

            <div className="divide-y divide-border/40">
                {loading ? (
                    <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
                ) : entries.length === 0 && !composerOpen ? (
                    <div className="p-10 text-center" data-testid={`case-notes-empty-${role}`}>
                        <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                            <Stethoscope size={20} weight="duotone" className="text-muted-foreground" />
                        </div>
                        <p className="text-sm text-foreground font-medium">No notes for this case yet</p>
                        <p className="text-xs text-muted-foreground mt-1 mb-4">
                            {role === "planner"
                                ? "Capture observations, decisions, or anything you want future-you to remember."
                                : "Capture observations, treatment decisions, or what the patient reports."}
                        </p>
                        <Button size="sm" variant="outline" onClick={() => setComposerOpen(true)} className="rounded-full" data-testid={`case-notes-add-first-${role}`}>
                            Add first note
                        </Button>
                    </div>
                ) : (
                    entries.map((entry) => (
                        <NoteRow key={entry.id} entry={entry} />
                    ))
                )}
            </div>
        </div>
    );
}

function NoteComposer({ caseId, endpointBase, onCancel, onSaved, role }) {
    const [body, setBody] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const save = async () => {
        const text = body.trim();
        if (!text || submitting) return;
        setSubmitting(true);
        try {
            await api.post(`/cases/${caseId}${endpointBase}`, { text });
            toast.success("Note saved");
            setBody("");
            onSaved && onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save note");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="px-6 py-5 bg-secondary/20 border-b border-border/40" data-testid={`case-note-composer-${role}`}>
            <Textarea
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={role === "planner"
                    ? "Note IPR / attachment decisions to double-check before sending plan. Anything you want future-you to remember."
                    : "What happened in this visit? What does the patient report? Treatment decisions, observations, next steps…"}
                className="text-sm"
                data-testid={`case-note-body-${role}`}
            />
            <div className="flex items-center justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={onCancel} className="rounded-full" data-testid={`case-note-cancel-${role}`}>
                    <CloseIcon size={12} weight="bold" className="mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={save} disabled={!body.trim() || submitting} className="rounded-full" data-testid={`case-note-save-${role}`}>
                    {submitting ? "Saving…" : "Save note"}
                </Button>
            </div>
        </div>
    );
}

function NoteRow({ entry }) {
    const created = new Date(entry.created_at);
    return (
        <div className="px-6 py-5 hover:bg-secondary/20 transition-colors" data-testid={`case-note-row-${entry.id}`}>
            <div className="flex items-center gap-2 flex-wrap text-xs mb-2">
                <span className="font-display text-base font-medium text-foreground tabular-nums">
                    {created.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground tabular-nums">
                    {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                </span>
                {entry.author_name && (
                    <>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{entry.author_name}</span>
                    </>
                )}
                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-foreground/5 text-foreground/70">
                    Note
                </span>
            </div>
            <div className="text-sm whitespace-pre-wrap text-foreground/90 leading-relaxed">{entry.text}</div>
        </div>
    );
}
